import React from 'react';

import { LoadingButton } from '@mui/lab';
import { SxProps } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';

import { GradientTypography } from './minicomponents';

interface GradientButtonProps {
  disabledTooltipText?: string;
  isLoading?: boolean;
  onSubmitButtonHandler?: () => void;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  href?: string;
  target?: string;
  rel?: string;
  component?: React.ElementType;
  iconLeft?: React.ReactElement;
  buttonText: string;
  sx?: SxProps<Theme>;
  version?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
}

export const GradientButton: React.FC<GradientButtonProps> = ({
  isLoading = false,
  onSubmitButtonHandler,
  iconLeft,
  buttonText,
  sx,
  version = 'contained',
  disabled = false,
  ...props
}) => {
  const theme = useTheme();

  const gradientBackground = `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`;

  const buttonStyles = {
    contained: {
      background: gradientBackground,
      color: 'white',
      '&:hover': {
        opacity: '80%!important',
        transition: 'none!important',
      },
    },
    outlined: {
      background: 'transparent',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: theme.palette.primary.main,
      '& .MuiButton-startIcon': {
        color: theme.palette.primary.main,
      },
      '&:hover': {
        background: gradientBackground,
        '& .MuiButton-startIcon': {
          color: 'white',
        },
        '& .MuiTypography-root': {
          color: 'white',
          background: 'none',
          WebkitBackgroundClip: 'unset',
          WebkitTextFillColor: 'white',
        },
      },
    },
    text: {
      background: 'transparent',
      padding: '0 16px',
      '& .MuiButton-startIcon': {
        color: theme.palette.primary.main,
      },
      '&:hover': {
        background: 'rgba(61,75,245,0.08)',
      },
    },
  };

  const gradientButton = (
    <LoadingButton
      sx={[
        {
          minWidth: 164,
          ...buttonStyles[version],
          '&.Mui-disabled': {
            background: theme.palette.secondary.light,
            color: theme.palette.primary.contrastText,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      variant={version}
      disabled={isLoading || disabled} // Only disable when loading or explicitly disabled
      onClick={onSubmitButtonHandler || props.onClick}
      startIcon={iconLeft}
      loading={isLoading}
      {...props}
    >
      {version === 'outlined' || version === 'text' ? (
        <GradientTypography variant="button">{buttonText}</GradientTypography>
      ) : (
        buttonText
      )}
    </LoadingButton>
  );

  // Always return the button without the Tooltip wrapper
  return gradientButton;
};
